import React, { CSSProperties, useEffect, useState } from 'react';
import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import { TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Error as KendoError } from '@progress/kendo-react-labels';
import { SvgIcon } from '@progress/kendo-react-common';
import { pinIcon, xIcon } from '@progress/kendo-svg-icons';
import EditorCard from '../EditorCard/EditorCard';
import styles from './_analyst-comments-editor.module.scss';

export interface AnalystComment {
  type: string;
  comments: string;
}

export interface AnalystCommentsEditorProps {
  onChange: (comments: AnalystComment[]) => void;
  style?: CSSProperties;
  initialComments?: AnalystComment[];
  /** Card title; defaults to "Analyst Comments". */
  title?: string;
  /**
   * The decision code's default comment. Shown as a pinned, read-only row so everything the dealer receives
   * is listed in one place; the caller appends it to the comments at submit time.
   */
  pinnedComment?: string;
}

const COMMENT_TYPES: Array<{ value: string; label: string }> = [
  { value: 'credit', label: 'Credit' },
  { value: 'docs', label: 'Docs' },
];

const typeLabel = (type: string) => COMMENT_TYPES.find(t => t.value === type)?.label ?? type;

export default function AnalystCommentsEditor(props: AnalystCommentsEditorProps) {
  const [comments, setComments] = useState<AnalystComment[]>(props.initialComments ?? []);
  const [commentText, setCommentText] = useState<string>('');
  const [commentType, setCommentType] = useState<string>('credit');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (props.initialComments && props.initialComments.length > 0 && comments && comments.length === 0) {
      setComments(props.initialComments);
    }
  }, [props.initialComments]);

  const updateComments = (updatedComments: AnalystComment[]) => {
    setComments(updatedComments);
    props.onChange(updatedComments);
  };

  const handleAdd = () => {
    const text = commentText.trim();
    if (text) {
      updateComments([...comments, { comments: text, type: commentType }]);
      setCommentText('');
      setErrorMessage('');
    } else {
      setErrorMessage('You must enter a note.');
    }
  };

  const handleRemove = (index: number) => {
    updateComments(comments.filter((_, i) => i !== index));
  };

  const totalCount = comments.length + (props.pinnedComment ? 1 : 0);

  return (
    <EditorCard
      title={props.title ?? 'Analyst Comments'}
      count={totalCount}
      titleStyle={props.style}
      className={styles.analystCommentsEditorContainer}
    >
      <div className={styles.composer}>
        <ButtonGroup className={styles.typeToggle}>
          {COMMENT_TYPES.map(type => (
            <Button
              key={type.value}
              togglable
              selected={commentType === type.value}
              themeColor={commentType === type.value ? 'primary' : 'base'}
              fillMode={commentType === type.value ? 'solid' : 'outline'}
              onClick={() => setCommentType(type.value)}
              aria-label={`${type.label} comment`}
            >
              {type.label}
            </Button>
          ))}
        </ButtonGroup>
        <TextBox
          id="note-text"
          className={styles.commentInput}
          value={commentText}
          placeholder="Add a comment for the dealer…"
          onChange={(e: TextBoxChangeEvent) => {
            setCommentText((e.target.value as string) ?? '');
            if (errorMessage) {
              setErrorMessage('');
            }
          }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          valid={!errorMessage}
        />
        <Button themeColor="primary" onClick={handleAdd} className={styles.addButton}>
          Add
        </Button>
      </div>
      {errorMessage && <KendoError className={styles.composerError}>{errorMessage}</KendoError>}
      <div className={styles.commentList}>
        {props.pinnedComment && (
          <div className={`${styles.commentRow} ${styles.commentRowPinned}`}>
            <span className={`${styles.typeTag} ${styles.typeTagPinned}`}>Default</span>
            <span className={styles.commentBody}>
              {props.pinnedComment}
              <span className={styles.commentCaption}>Default for this decision &middot; sent automatically</span>
            </span>
            <SvgIcon icon={pinIcon} className={styles.pinIcon} />
          </div>
        )}
        {comments.map((comment, index) => (
          <div className={styles.commentRow} key={`${index}-${comment.type}`}>
            <span className={styles.typeTag}>{typeLabel(comment.type)}</span>
            <span className={styles.commentBody}>{comment.comments}</span>
            <Button
              fillMode="flat"
              size="small"
              svgIcon={xIcon}
              className={styles.removeButton}
              onClick={() => handleRemove(index)}
              title="Remove comment"
              aria-label="Remove comment"
            />
          </div>
        ))}
        {totalCount === 0 && <span className={styles.emptyList}>No comments yet</span>}
      </div>
    </EditorCard>
  );
}
